import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Company } from '@/context/types';
import { Row } from '@/components/SettingsLayout';
import { Text } from '@/ui';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';
import { formatDateToNow } from '@/utils/helpers/dateFormater';
import { formatCurrency } from '@/utils/currency';
import { useGetUser } from '@/hooks';
import { FINAL_USERS } from '@/enums';
import { DataTable } from '@/components/data-table';
import type { CardField } from '@/components/data-table';
import styles from './styles.module.css';
import { CurrencyCode } from '@final-commerce/common';

export type CompanyListRow = Company & {
  organization?: string;
  organizationLogo?: string;
  country?: string;
  email?: string;
  updatedAt?: string;
  processingVolume?: number;
  numberOfOutlets?: number;
  plan?: { name?: string };
  currency?: string;
};

type Props = {
  companies: CompanyListRow[];
};

function CompaniesTable({ companies }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useGetUser();
  const rootUser = FINAL_USERS.includes(currentUser.type);

  const columnHelper = createColumnHelper<CompanyListRow>();

  const mobileCardFields = useMemo<CardField<CompanyListRow>[]>(() => {
    const fields: CardField<CompanyListRow>[] = [
      { key: 'name', primary: true, render: (row) => row.original.name },
      { key: 'country', label: t('Shared.country'), render: (row) => row.original.country ?? '-' },
      { key: 'email', label: t('Shared.email'), render: (row) => row.original.email ?? '-' },
      {
        key: 'updatedAt',
        label: t('Companies.last_seen'),
        render: (row) => {
          const v = row.original.updatedAt;
          return v ? formatDateToNow(v) : '-';
        },
      },
      {
        key: 'plan',
        label: t('Companies.plan'),
        render: (row) => (row.original as CompanyListRow).plan?.name ?? '-',
      },
      {
        key: 'processingVolume',
        label: t('Companies.processing_volume'),
        render: (row) => {
          const c = row.original as CompanyListRow;
          return c?.currency
            ? formatCurrency(c.processingVolume ?? 0, c.currency)
            : String(c?.processingVolume ?? 0);
        },
      },
      {
        key: 'numberOfOutlets',
        label: t('Outlets.outlets'),
        render: (row) => String((row.original as CompanyListRow).numberOfOutlets ?? 0),
      },
    ];
    if (rootUser) {
      fields.splice(1, 0, {
        key: 'organization',
        label: t('Shared.organization'),
        render: (row) => row.original.organization ?? '-',
      });
    }
    return fields;
  }, [t, rootUser]);

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor((c) => c.name, {
        id: 'name',
        header: () => (
          <Row gapSize='none'>
            <Text size='xs' weight='light-medium'>
              {t('Shared.name')}
            </Text>
          </Row>
        ),
        cell: ({ row }) => (
          <Row inlineRow>
            <ImageWithFallback src={row.original.logo} thumbnail width={48} height={48} />
            <Text size='xs' weight='light-medium' color='blue'>
              {row.original.name}
            </Text>
          </Row>
        ),
        size: 200,
      }),
      ...(rootUser
        ? [
            columnHelper.accessor((c) => c.organization, {
              id: 'organization',
              header: () => (
                <Row gapSize='none'>
                  <Text size='xs' weight='light-medium'>
                    {t('Shared.organization')}
                  </Text>
                </Row>
              ),
              cell: ({ row }) => (
                <Row inlineRow>
                  {row.original.organization && (
                    <div className={styles.imgContainer} style={{ width: 24, height: 24 }}>
                      <img
                        src={row.original.organizationLogo || '/placeholder.svg'}
                        alt={row.original.organization}
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                  )}
                  <Text size='xs' weight='light-medium'>
                    {row.original.organization ?? '-'}
                  </Text>
                </Row>
              ),
              size: 120,
            }),
          ]
        : []),
      columnHelper.accessor((c) => c.country, {
        id: 'country',
        header: () => (
          <Row gapSize='none'>
            <Text size='xs' weight='light-medium'>
              {t('Shared.country')}
            </Text>
          </Row>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {getValue() ?? '-'}
          </Text>
        ),
        size: 80,
      }),
      columnHelper.accessor((c) => c.email, {
        id: 'email',
        header: () => (
          <Row gapSize='none'>
            <Text size='xs' weight='light-medium'>
              {t('Shared.email')}
            </Text>
          </Row>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {getValue() ?? '-'}
          </Text>
        ),
        size: 160,
      }),
      columnHelper.accessor((c) => c.updatedAt, {
        id: 'updatedAt',
        header: () => (
          <Row gapSize='none'>
            <Text size='xs' weight='light-medium'>
              {t('Companies.last_seen')}
            </Text>
          </Row>
        ),
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <Text size='xs' weight='light-medium'>
              {v ? formatDateToNow(v) : '-'}
            </Text>
          );
        },
        size: 100,
      }),
      columnHelper.accessor((c) => c.createdAt, {
        id: 'createdAt',
        header: () => (
          <Row gapSize='none'>
            <Text size='xs' weight='light-medium'>
              {t('Companies.created')}
            </Text>
          </Row>
        ),
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <Text size='xs' weight='light-medium'>
              {v ? formatDateToNow(v) : '-'}
            </Text>
          );
        },
        size: 100,
      }),
      columnHelper.accessor((c) => (c as CompanyListRow).processingVolume, {
        id: 'processingVolume',
        header: () => (
          <Text size='xs' weight='light-medium'>
            {t('Companies.processing_volume')}
          </Text>
        ),
        cell: ({ row }) => {
          const c = row.original as CompanyListRow;
          return (
            <Text size='xs' weight='light-medium'>
              {c?.currency
                ? formatCurrency(c.processingVolume ?? 0, c.currency as CurrencyCode)
                : String(c?.processingVolume ?? 0)}
            </Text>
          );
        },
        size: 120,
      }),
      columnHelper.accessor((c) => (c as CompanyListRow).numberOfOutlets, {
        id: 'numberOfOutlets',
        header: () => (
          <Row gapSize='none'>
            <Text size='xs' weight='light-medium'>
              {t('Outlets.outlets')}
            </Text>
          </Row>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {(getValue() ?? 0).toString()}
          </Text>
        ),
        size: 60,
      }),
      columnHelper.accessor((c) => (c as CompanyListRow).plan?.name, {
        id: 'plan',
        header: () => (
          <Row gapSize='none' justify='justify-start'>
            <Text size='xs' weight='light-medium'>
              {t('Companies.plan')}
            </Text>
          </Row>
        ),
        cell: ({ getValue }) => (
          <Text size='xs' weight='light-medium'>
            {getValue() ?? '-'}
          </Text>
        ),
        size: 100,
      }),
    ];
    return cols;
  }, [t, rootUser, columnHelper]);

  const table = useReactTable({
    data: companies,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DataTable
      table={table}
      mobileCardFields={mobileCardFields}
      mobileGetImageUrl={(row) => row.original.logo}
      onRowClick={(row) => {
        navigate(`${pathname}/${row.original._id}`);
      }}
    />
  );
}

export default CompaniesTable;
